import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Hook to load and setup the initial welcome conversation
 */
export const useInitialConversation = ({
  loadInitialConversation,
  convo,
  restoreConversation,
  enableAutoScroll,
  setInitialConversationLoaded
}) => {
  const [searchParams] = useSearchParams();
  const welcomeParam = searchParams.get('welcome') === 'true';
  // Prevents re-entry: once a load is triggered for a given welcomeParam value,
  // convo.length changes (from restoreConversation) don't retrigger the fetch.
  const loadTriggeredRef = useRef(false);

  useEffect(() => {
    // Reset guard when welcomeParam changes (e.g. user navigates to welcome again)
    loadTriggeredRef.current = false;
  }, [welcomeParam]);

  useEffect(() => {
    // Check localStorage flag to ensure we only load once ever (unless welcome=true)
    const LOAD_FLAG = 'plex-initial-conversation-loaded';
    const hasLoaded = localStorage.getItem(LOAD_FLAG) === 'true';

    // Only load if:
    // 1. (loadInitialConversation flag is true OR welcome=true param) AND
    // 2. We haven't loaded it yet (localStorage flag not set) OR welcome=true AND
    // 3. There's no existing conversation (skip this check for explicit welcome=true,
    //    since restoreConversation replaces turns entirely)
    const shouldLoad = (loadInitialConversation || welcomeParam) &&
                       (!hasLoaded || welcomeParam) &&
                       (welcomeParam || convo.length === 0);

    if (shouldLoad && !loadTriggeredRef.current) {
      loadTriggeredRef.current = true;
      console.log('[InitialConversation] Loading...');
      // Set flag BEFORE loading to prevent race conditions
      localStorage.setItem(LOAD_FLAG, 'true');

      // Note: We intentionally do NOT remove the welcome=true parameter from the URL
      // so that page refreshes will preserve the welcome conversation

      // Load the conversation from /initial-conversation.json and /initial-conversation.md
      const fetchOrThrow = (url, parse) => fetch(url).then(r => {
        if (!r.ok) {
          throw new Error(`${url} returned ${r.status}`);
        }
        return parse(r);
      });
      Promise.all([
        fetchOrThrow('/initial-conversation.json', r => r.json()),
        fetchOrThrow('/initial-conversation.md', r => r.text())
      ])
        .then(([conversation, markdown]) => {
          // Inject the markdown content into the assistant turn; rawMarkdown is a rendering hint
          // that bypasses prepareMarkdown/optimizeForStreaming to preserve raw HTML in the template.
          // LLMClient.js strips rawMarkdown before any API calls so it won't be sent to Anthropic.
          if (conversation.turns && conversation.turns[1] && conversation.turns[1].role === 'assistant') {
            const block = conversation.turns[1].content[0];
            block.text = markdown;
            block.rawMarkdown = true;
          }
          const validated = validateContext(conversation);
          if (validated) {
            setInitialConversationLoaded(true);
            setTimeout(() => {
              restoreConversation(validated);
              // Enable autoscroll after a delay to ensure content is fully rendered
              // This allows the first render to stay at scroll position 0
              setTimeout(() => {
                enableAutoScroll();
                // Setup welcome onboarding after DOM is rendered
                setTimeout(() => {
                  const onboarding = document.querySelector('.welcome-onboarding');
                  if (onboarding) {
                    console.debug('[InitialConversation] Triggering onboarding setup');
                    // Dispatch custom event that the useEffect can listen for
                    window.dispatchEvent(new CustomEvent('welcome-onboarding-ready'));
                  }
                }, 100);
              }, 500);
            }, 100);
          } else {
            console.error('Invalid conversation format in initial-conversation.json');
            toast.error('Failed to load initial conversation');
          }
        })
        .catch(error => {
          console.error('Failed to load initial conversation:', error);
          toast.error(`Failed to load conversation: ${error.message}`);
        });
    }
  }, [loadInitialConversation, welcomeParam, convo.length, restoreConversation, enableAutoScroll, setInitialConversationLoaded]);
};

/**
 * Validate conversation context structure
 */
const validateContext = (conversation) => {
  if (!conversation || !conversation.turns || !Array.isArray(conversation.turns)) {
    return null;
  }
  return conversation;
};

/**
 * Hook to setup welcome onboarding interactions
 */
export const useWelcomeOnboarding = (inputRef) => {
  useEffect(() => {
    let isSetup = false;

    const setupWelcomeOnboarding = () => {
      if (isSetup) return; // Only setup once

      const onboarding = document.querySelector('.welcome-onboarding');
      if (!onboarding) {
        console.warn('[WelcomeOnboarding] .welcome-onboarding not found in DOM');
        return;
      }

      isSetup = true;

      // Default prompts shown initially (one from each category)
      const defaultPrompts = [
        'Find targets for imatinib based on its chemical structure and known activities',
        'What biological pathways are associated with the BRCA1 and BRCA2 genes?',
        'Identify biomarkers from a gene expression profile for lung cancer treatment',
        'Compare cell lines responsive vs non-responsive to EGFR inhibitors'
      ];

      // Card-specific prompts shown when a card is selected
      const cardPrompts = {
        targets: [
          'Find targets for imatinib based on its chemical structure and known activities',
          'Predict the mechanism of action for a compound similar to erlotinib',
          'What are the known targets of compounds structurally similar to vemurafenib?'
        ],
        disease: [
          'What biological pathways are associated with the BRCA1 and BRCA2 genes?',
          'Characterize the disease processes from this gene expression signature: TP53, MYC, EGFR',
          'Connect the PI3K pathway genes to therapeutic opportunities in breast cancer'
        ],
        biomarkers: [
          'Identify biomarkers from a gene expression profile for lung cancer treatment',
          'Find genes consistently regulated across KRAS-mutant cell lines under chemotherapy',
          'Discover PD markers for measuring MEK inhibitor therapeutic effects'
        ],
        compounds: [
          'Compare cell lines responsive vs non-responsive to EGFR inhibitors',
          'Search for compounds structurally similar to dasatinib and their known targets',
          'Leverage CCLE data to find genetic features predicting response to paclitaxel'
        ]
      };

      const cardLabels = {
        targets: 'find targets',
        disease: 'analyze disease & pathways',
        biomarkers: 'discover biomarkers',
        compounds: 'explore compounds & cell lines'
      };

      // Build prompt chips using DOM APIs to avoid innerHTML injection
      const renderPromptChips = (container, prompts) => {
        container.textContent = '';
        prompts.forEach(prompt => {
          const btn = document.createElement('button');
          btn.className = 'welcome-prompt-chip';
          btn.dataset.prompt = prompt;
          const arrow = document.createElement('span');
          arrow.className = 'welcome-prompt-chip__arrow';
          arrow.textContent = '\u2192';
          btn.appendChild(arrow);
          btn.appendChild(document.createTextNode(' ' + prompt));
          container.appendChild(btn);
        });
      };

      // Handle card clicks
      // Note: .welcome-onboarding is just the intro text block, cards are siblings in the DOM
      const cards = document.querySelectorAll('.welcome-card');
      const promptsContainer = document.getElementById('welcome-prompts-container');
      const promptsLabel = document.getElementById('welcome-prompts-label');


      cards.forEach(card => {
        card.addEventListener('click', (e) => {
          e.stopPropagation();
          const cardId = card.dataset.card;

          // Check if this card was already active
          const wasActive = card.classList.contains('welcome-card--active');

          // Remove active state from all cards
          cards.forEach(c => c.classList.remove('welcome-card--active'));

          // If it wasn't active, make it active
          if (!wasActive) {
            card.classList.add('welcome-card--active');
          }

          // Clear the input field when card selection changes
          if (inputRef?.current) {
            inputRef.current.value = '';
            inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
          }

          // Update prompts - show default prompts when deselecting, card-specific when selecting
          const prompts = wasActive ? defaultPrompts : cardPrompts[cardId];
          const label = wasActive ? 'TRY AN EXAMPLE' : `Try asking about ${cardLabels[cardId]}`;

          if (promptsLabel) promptsLabel.textContent = label.toUpperCase();
          if (promptsContainer) {
            renderPromptChips(promptsContainer, prompts);
            // Re-attach prompt chip handlers after updating DOM
            attachPromptHandlers();
          }
        });
      });

      // Handle prompt chip clicks
      const attachPromptHandlers = () => {
        const chips = document.querySelectorAll('.welcome-prompt-chip');
        chips.forEach(chip => {
          chip.addEventListener('click', () => {
            const prompt = chip.dataset.prompt;
            if (prompt && inputRef?.current) {
              inputRef.current.value = prompt;
              inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
              inputRef.current.focus();
            }
          });
        });
      };
      attachPromptHandlers();

      // Handle expand/collapse toggle
      const toggleBtn = document.querySelector('[data-toggle-details]');
      const details = document.getElementById('welcome-details');
      if (toggleBtn && details) {
        toggleBtn.addEventListener('click', () => {
          const isHidden = details.style.display === 'none';
          details.style.display = isHidden ? 'block' : 'none';
          toggleBtn.textContent = isHidden ? 'Hide full capabilities ↑' : 'View full capabilities ↓';
        });
      }

      // Select the first card by default
      if (cards.length > 0) {
        const firstCard = cards[0];
        const firstCardId = firstCard.dataset.card;
        firstCard.classList.add('welcome-card--active');

        // Update prompts and label for first card
        const prompts = cardPrompts[firstCardId];
        const label = `Try asking about ${cardLabels[firstCardId]}`;

        if (promptsLabel) promptsLabel.textContent = label.toUpperCase();
        if (promptsContainer) {
          renderPromptChips(promptsContainer, prompts);
          // Re-attach prompt chip handlers after updating DOM
          attachPromptHandlers();
        }
      }

    };

    // Try to setup immediately if element exists
    let intervalId;
    const checkAndSetup = () => {
      const onboarding = document.querySelector('.welcome-onboarding');
      if (onboarding && !isSetup) {
        setupWelcomeOnboarding();
        clearInterval(intervalId); // stop polling once setup succeeds
      }
    };

    // Check immediately
    checkAndSetup();

    // Also check periodically for first 3 seconds
    intervalId = setInterval(checkAndSetup, 200);
    setTimeout(() => clearInterval(intervalId), 3000);

    // Listen for custom event from initial conversation load
    const handleWelcomeReady = () => {
      setupWelcomeOnboarding();
    };

    window.addEventListener('welcome-onboarding-ready', handleWelcomeReady);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('welcome-onboarding-ready', handleWelcomeReady);
    };
  }, [inputRef]);
};